import React, { lazy, PureComponent, Suspense } from "react";
import type { Node } from "react";
import { socialSharingList, handleShareClick } from "@wirewaxorg/social";
import _ from 'lodash';
import VideoLoader from "../videoLoader";
import ImageLoader from "../imageLoader";
import Meta from "../meta";
import {
  toggleFullScreen,
  getBrowserVisibilityValues,
} from "../../utils/domEvents";
import App from "../../app/app.ui";
import type { Props, State } from "./loadOptions.types";
import ScreenWrapper from "../screenWrapper/screenWrapper.container";
import { ControlsWrapper, Wrapper, AppWrapper } from "./loadOptions.styles";
import WhiteLabel from "../whitelabel";
import {
  getPlayerConfiguration,
  getThirdPartyPlayerKey,
  getThirdPartyPlayerOptions,
} from "./loadOptions.helpers";
import EventManager from "../eventManager/eventManager";
import EndFrame from "../endFrame";
import utils from "../../utils";
import Debugger from "../debugger";
import { isMobileDevice, isiPhoneiPod } from "../../utils/isMobileDevice";
import componentOptionChecker from "../../utils/componentOptionChecker";
import Logger from "../../utils/Logger";
import { END_FRAME } from "../../state/options/options.constant";
import IeMessageBanner from '../messageBanner/ieMessageBanner.ui';

const Controls = lazy(() => import("@wirewaxorg/controls"));

class LoadOptions extends PureComponent<Props, State> {
  constructor(props: Object) {
    super(props);

    const {
      playerParams,
      playerParams: { customPlayer },
      userAgent: { isIOS },
    } = this.props;

    this.playerParams = playerParams;
    this.browserVisibility = getBrowserVisibilityValues();

    this.state = {
      hideMainPlayButton: this.playerParams.autoplay,
      showMuteButton: this.playerParams.autoplay && !this.playerParams.hideAudioIcon,
      isPlayingOnFullScreenToggle: null,
    };

    this.childRef = React.createRef();
    this.isIosDefaultPlayer = isIOS && !customPlayer;
  }

  componentDidMount() {
    // Fetch vidData.json
    const {
      id,
      getOptions,
      version,
    } = this.props;

    getOptions(id, version);

    document.addEventListener(
      this.browserVisibility.visibilityChange,
      this.handleVisibilityChange,
      false,
    );
  }

  componentDidUpdate(prevProps: Object) {
    const { mainVideo, playback } = this.props;

    if (mainVideo !== prevProps.mainVideo) {
      // check to update this.playerParams with playerConfigurations
      const {
        video: { playerConfiguration = {} }
      } = mainVideo;

      this.playerParams = playerConfiguration
        ? componentOptionChecker({
            ...this.playerParams,
            ...getPlayerConfiguration(playerConfiguration || {})
          })
        : this.playerParams;
    }

    // TODO: This is a walkaround for redux stage management
    // Currently our playback is not managed based on state
    // Which makes it very messy that play, pause, skip functions are passed
    // It has both update state and control player ref to work
    // Ideally, loadOption.play should only change playback states
    // And in this checking to call player ref to change playback
    const { isPlaying, isSeeking, seekTo } = prevProps.playback;

    if (isPlaying && !playback.isPlaying) {
      this.pause();
    }

    if (!isPlaying && playback.isPlaying) {
      this.play();
    }

    if (isSeeking) this.skip(seekTo);
  }

  componentWillUnmount() {
    document.removeEventListener(
      this.browserVisibility.visibilityChange,
      this.handleVisibilityChange,
    );
  }

  // Passed to video player, used in play and toggle play functions
  hidePlayButton = () => {
    const { hideMainPlayButton } = this.state;

    if (!hideMainPlayButton) {
      this.setState({ hideMainPlayButton: true });
    }
  };

  hideMuteIcon = () => {
    const { showMuteButton } = this.state;

    if (showMuteButton) {
      this.setState({ showMuteButton: false });
    }
  };

    // Passed to player to reload video and resume to the current time
    reload = () => {
        this.childRef.current._reload();
    }

  // Passed to App, onClick of play icon, fired on fullscreencheck and close overlay to resume playback
  play = () => {
    const { hideMainPlayButton } = this.state;
    const {
      activeVideo: {
        video: { mediaType },
      },
      playing,
    } = this.props;
    const isImage = mediaType;
    if (playing) return;

    if (!hideMainPlayButton) {
      this.hidePlayButton();
    }

    if (isImage) return;

    this.childRef.current._play();
  };

  // Passed to App and controls
  togglePlay = () => {
    const { hideMainPlayButton } = this.state;

    if (!hideMainPlayButton) {
      this.hidePlayButton();
    }

    this.childRef.current._togglePlay();
  };

  // Passed to app and controls, fired in visibility check and open overlay
  pause = () => {
    if (this.childRef.current) {
      this.childRef.current._pause();
    }
  };

  // Passed to app and controls
  skip = (to: number, format: string = "fps") => {
    this.childRef.current._skip(to, format);
    const { reduxSeeked } = this.props;
    reduxSeeked();
  };

  toggleUnmuteButton = (flag: boolean) => {
    this.setState({ showMuteButton: flag });
  };

  // Passed to controals, used in unMute functio
  mute = () => {
    const { showMuteButton } = this.state;
    if (showMuteButton) {
      this.hideMuteIcon();
    }

    this.childRef.current._mute();
  };

  // Passed to controls
  setVolume = (vol: number) => {
    // note: move showMuteButton to redux state and access from controls?
    const { showMuteButton } = this.state;
    if (showMuteButton) {
      this.hideMuteIcon();
    }

    this.childRef.current._setVolume(vol);
  };

  // Passed to controls
  selectSubtitle = (subtitle: string) => {
    this.childRef.current._selectSubtitle(subtitle);
  };

  // Passed to controls
  setVideoQuality = (quality: number) => {
    this.childRef.current._setStreamingQuality(quality);
  };

  // Passed to controls
  remainingTime = () => this.childRef.current._remainingTime();

  // Passed to controls
  bufferedPercent = () => this.childRef.current._bufferedPercent();

  videoDuration = () => this.childRef.current && this.childRef.current._duration();

  // On click of end frame replay
  restart = () => {
    this.childRef.current._restart();
  };

  // Not used - props.switchVideo is passed to player instead
  switchVideo = (video: Object, frame: number) => {
    this.childRef.current._switchVideo(video, frame);
  };

  // Passed to player and controls
  isPlayingOnFullScreenToggle = () => {
    const { playing } = this.props;

    this.setState({ isPlayingOnFullScreenToggle: playing });

    toggleFullScreen(this.videoWrapper);
  };

  unMute = () => {
    this.mute();
  };

    // Fired in event listener for browser visibility change
    handleVisibilityChange = () => {
        const { hasEnded, isFullScreen, toggleControlsVisibility } = this.props;
        const { isPlayingOnFullScreenToggle } = this.state;
        const { forceVisible } = this.playerParams;
        const hidden = forceVisible ? false : document[this.browserVisibility.hidden];

        if (hidden && !hasEnded && !isFullScreen && !isPlayingOnFullScreenToggle) {
            // IOS HTML5 video has stalled issue, set up to reload the video to bypass the issue
            if (this.isIosDefaultPlayer) {
                this.reload();
                toggleControlsVisibility(false);
                this.setState({ hideMainPlayButton: false });
            } else {
                this.pause();
            }
        }

        if (isPlayingOnFullScreenToggle) {
            this.setState({ isPlayingOnFullScreenToggle: false });
        }
    };

  // Passed to app and player
  closeOverlayAndAnimateOut = () => {
    // This function is passed to the repos that need to fire a close overlay event.
    // The Overlay component animates out the overlay based on the isOverlayClosed value
    // If the activeOverlay is set to {} and isOverlayClosed is set to true at once
    // The overlay will not animate out
    // This is why we set isOverlayClosed to true and wait for 200ms to set activeOverlay to {}
    const { toggleOverlay, closeOverlayBool } = this.props;

    closeOverlayBool();

    this.close = setTimeout(() => {
      toggleOverlay({});
      clearTimeout(this.close);
    }, 400);
  };

  // Passed to controls
  openEmbedOverlay = () => {
    const { toggleEmbedOverlay } = this.props;

    this.pause();
    toggleEmbedOverlay(true);
  };

  // Passed to app and controls
  closeEmbedOverlay = () => {
    const { toggleEmbedOverlay } = this.props;

    // use setTimeout when applying animation
    toggleEmbedOverlay(false);
    this.play();
  };

  // Third Party Player
  getThirdPartyPlayerConfig = (): Object => {
    const { customPlayer, customPlayerVidId } = this.playerParams;
    const { activeVideo: { video: { tpPlayers = [] } } } = this.props;

    // No player params, use default player
    if (!customPlayer) return {};

    // VidData.json third Party Player config has priority
    if (tpPlayers.length > 0) {
      return getThirdPartyPlayerOptions(customPlayer, tpPlayers);
    }

    // Has player params & no vidData & no v(vidId) params, use default player
    if (!customPlayerVidId) return {}

    return {
      tpPlayer: getThirdPartyPlayerKey(customPlayer),
      tpVidId: customPlayerVidId,
    }
  }

  videoWrapper: Node;

  childRef: any;

  controls: boolean;

  share: boolean;

  playerParams: Object;

  browserVisibility: Object;

  isIosDefaultPlayer: boolean;

  render = () => {
    const {
      activeVideo: {
        hotspots,
        hotspotDesign,
        interactions,
        timeTriggers,
        overlays,
        video,
        subtitles,
      },
      frame,
      subtitle,
      location,
      volume,
      play,
      pause,
      setVolume,
      currentFrame,
      currentTime,
      selectSubtitle,
      mute,
      toggleOverlay,
      loaded,
      player,
      playerReady,
      playing,
      activeOverlay,
      switchVideo,
      videoType,
      endFrameVideo,
      mainVideo,
      endEndFrame,
      endFrameEnded,
      setDimension,
      width,
      height,
      setVideoQuality,
      videoQuality,
      seek,
      hotspotIsClicked,
      hotspotClickout,
      overlayEvent,
      isFullScreen,
      isOverlayClosed,
      closeOverlayBool,
      hasPlayed,
      hasEnded,
      forceSpriteIdShown,
      setForceSpriteIdShown,
      embedOverlay,
      fullScreen,
      setMousePosition,
      animationEnded,
      animationPlaying,
      prevFrame,
      toggleControlsVisibility,
      showControls,
      setVideoRenditions,
      videoRenditions,
      userAgent,
      userAgent: {
        isFrameNotAccurate,
        isIE11,
        isIE,
      },
      uuId,
      apiRejected,
      id,
    } = this.props;

    const { activeVideo } = this.props;

    const {
      duration,
      whiteLabel,
      fps,
      tagId,
      renditionsArray,
      aspectRatio,
      posterFrame,
      vidName,
      description,
      tpPlayers=[],
    } = video;

    const { hideMainPlayButton, showMuteButton } = this.state;
    const {
      noControls,
      transparent,
      forceRendition,
      fullBleed,
      noCustomisations,
      triggerSpriteId,
      disableMobileFullScreen,
      preventPauseOnClick,
      customParams, // Used by DCO
      autoplay,
      noMessage,
    } = this.playerParams;

    const renditions = videoRenditions.length > 0 ? videoRenditions : [
      ...new Set(renditionsArray.map(x => x.h).filter(x => x))
    ];

    const isImage = !!video.mediaType;
    const isOverlayOpen = Object.entries(activeOverlay).length > 0;
    const imageSource =
      renditionsArray && renditionsArray.length
        ? renditionsArray.filter(
            x => x.h === [...renditionsArray].pop().h
          )[0].src
        : "";

    const { tpPlayer, tpVidId } = this.getThirdPartyPlayerConfig();

    const shouldActivateEndframe = check => videoType === END_FRAME && utils.whiteLabelChecker(check);

    const hideControls = isOverlayOpen
    || noControls
    || (!isImage && (!player || !hideMainPlayButton))
    || shouldActivateEndframe(whiteLabel)
    || (!_.isEmpty(tpPlayer));

    const excludeEmbed = (shareList, shouldExclude) => shouldExclude ? shareList.filter(shareOption => shareOption.name !== 'Embed') : shareList;
    const hasHls = renditionsArray.find(rendition => rendition.ext === "m3u8");

    return loaded ? (
      <Wrapper transparent={transparent}>
        <EventManager
          toggleUnmuteButton={this.toggleUnmuteButton}
          autoplay={autoplay}
          isPlayerReady={player}
          play={this.play}
          pause={this.pause}
          skip={this.skip}
          video={video}
          id={id}
          uuId={uuId}
          currentTime={currentTime}
          mute={this.mute}
          volume={volume}
          setVolume={this.setVolume}
          interactions={interactions}
          hotspots={hotspots}
          hotspotIsClicked={hotspotIsClicked}
          overlays={overlays}
          activeOverlay={activeOverlay}
          toggleOverlay={toggleOverlay}
          playing={playing}
          hasPlayed={hasPlayed}
          videoQuality={videoQuality}
          setVideoQuality={this.setVideoQuality}
          fullScreen={this.isPlayingOnFullScreenToggle}
        />
        <Meta
          title={vidName}
          image={posterFrame && posterFrame.medium}
          description={description}
          id={id}
        />
        {shouldActivateEndframe(whiteLabel) && (
          <EndFrame
            isFullScreen={isFullScreen}
            isPlayingOnFullScreenToggle={this.isPlayingOnFullScreenToggle}
            fullScreen={fullScreen}
            restart={this.restart}
            width={width}
            height={height}
          />
        )}
        <ScreenWrapper
          aspectRatio={aspectRatio}
          ref={el => (this.videoWrapper = el)}
          playerWidth={width}
          playerHeight={height}
          setDimension={setDimension}
          fullBleed={fullBleed}
          fullScreen={fullScreen}
          isFullScreen={isFullScreen}
          animationEnded={animationEnded}
          animationPlaying={animationPlaying}
          setMousePosition={setMousePosition}
        >
          {isImage ? (
            <ImageLoader src={imageSource} />
          ) : (
            <VideoLoader
              options={this.playerParams}
              ref={this.childRef}
              fullScreen={this.isPlayingOnFullScreenToggle}
              hidePlayButton={this.hidePlayButton}
              closeOverlayAndAnimateOut={this.closeOverlayAndAnimateOut}
              playIconAction={this.play}
              muteIconAction={this.mute}
              restart={this.restart}
              showMuteButton={showMuteButton}
              hideMainPlayButton={hideMainPlayButton}
              hideMainPlayButtonIcon={this.playerParams.hideMainPlayButton}
              subtitle={subtitle}
              subtitles={subtitles}
              video={video}
              volume={volume}
              location={location}
              fps={fps}
              play={play}
              pause={pause}
              setVolume={setVolume}
              currentFrame={currentFrame}
              selectSubtitle={selectSubtitle}
              playerReady={playerReady}
              mute={mute}
              frame={frame}
              switchVideo={switchVideo}
              videoType={videoType}
              endFrameVideo={endFrameVideo}
              mainVideo={mainVideo}
              endEndFrame={endEndFrame}
              endFrameEnded={endFrameEnded}
              setVideoQuality={setVideoQuality}
              setVideoRenditions={setVideoRenditions}
              seek={seek}
              hideMuteIcon={this.hideMuteIcon}
              isOverlayOpen={isOverlayOpen}
              renditionsArray={renditionsArray}
              animationEnded={animationEnded}
              animationPlaying={animationPlaying}
              hasHls={hasHls}
              forceRendition={forceRendition}
              showControls={showControls}
              addAdditionalFramesToSkip={isFrameNotAccurate}
              apiRejected={apiRejected}
              customPlayer={tpPlayer}
              tpVidId={tpVidId}
              userAgent={userAgent}
            />
          )}
          {/*
            @pradeep customPlayer check temporarily in place
            to prevent render of play button/App
          */}
          {!videoType &&
          // !(customPlayer && SUPPORTED_PLAYERS.includes(customPlayer)) ? (
          //   <>
              <AppWrapper customPlayer={tpPlayer}>
                <App
                  className="app"
                  play={this.play}
                  pause={this.pause}
                  togglePlay={
                    preventPauseOnClick ? () => {} : this.togglePlay
                  }
                  skip={this.skip}
                  video={video}
                  hotspots={hotspots}
                  interactions={interactions}
                  timeTriggers={timeTriggers}
                  currentFrame={frame}
                  currentTime={currentTime}
                  fps={fps}
                  id={id}
                  overlays={overlays}
                  toggleOverlay={toggleOverlay}
                  activeOverlay={activeOverlay}
                  hotspotDesign={hotspotDesign}
                  tagId={tagId}
                  playing={playing}
                  hotspotIsClicked={hotspotIsClicked}
                  hotspotClickout={hotspotClickout}
                  overlayEvent={overlayEvent}
                  isOverlayClosed={isOverlayClosed}
                  closeOverlayBool={closeOverlayBool}
                  closeOverlayAndAnimateOut={this.closeOverlayAndAnimateOut}
                  triggerSpriteId={triggerSpriteId}
                  forceSpriteIdShown={forceSpriteIdShown}
                  setForceSpriteIdShown={setForceSpriteIdShown}
                  embedOverlay={embedOverlay}
                  closeEmbedOverlay={this.closeEmbedOverlay}
                  isImage={isImage}
                  prevFrame={prevFrame}
                  noCustomisations={noCustomisations}
                  hideMainPlayButton={hideMainPlayButton}
                  playerHeight={height}
                  playerWidth={width}
                  customParams={customParams} // DCO
                  customPlayer={tpPlayer}
                />
                <Debugger
                  hasPlayed={hasPlayed}
                  id={id}
                  videoQuality={videoQuality}
                  activeVideo={activeVideo}
                  activeOverlay={activeOverlay}
                  frame={frame}
                  currentFrame={currentFrame}
                  currentTime={currentTime}
                  play={this.play}
                  skip={this.skip}
                  toggleOverlay={toggleOverlay}
                  pause={this.pause}
                />
              </AppWrapper>
          //   </>
          // ) : (
          //   ""
          // )
          }
          {!hideControls ? (
            <Suspense fallback="">
              <ControlsWrapper fullBleed={fullBleed}>
                <Controls
                  className="controls"
                  pause={this.pause}
                  togglePlay={this.togglePlay}
                  mute={this.mute}
                  fullScreen={this.isPlayingOnFullScreenToggle}
                  setVolume={this.setVolume}
                  skip={this.skip}
                  selectSubtitle={this.selectSubtitle}
                  remainingTime={this.remainingTime}
                  options={this.playerParams}
                  currentTime={currentTime}
                  duration={duration}
                  videoDuration={this.videoDuration}
                  id={id}
                  hotspots={hotspots}
                  handleShareClick={handleShareClick}
                  socialSharingList={excludeEmbed(socialSharingList, isMobileDevice)}
                  playing={playing}
                  prevFrame={prevFrame}
                  volume={volume}
                  buffered={this.bufferedPercent}
                  selectedSubtitle={subtitle}
                  subtitles={subtitles
                    .map(track => track.label)
                    .filter(sub => sub)}
                  setVideoQuality={this.setVideoQuality}
                  renditions={renditions}
                  selectedQuality={videoQuality}
                  hasHls={hasHls}
                  fps={fps}
                  isFullScreen={isFullScreen}
                  isOverlayOpen={isOverlayOpen}
                  openEmbedOverlay={this.openEmbedOverlay}
                  closeEmbedOverlay={this.closeEmbedOverlay}
                  disableMobileFullScreen={disableMobileFullScreen}
                  isImage={isImage}
                  isMobileDevice={isMobileDevice}
                  isiPhoneiPod={isiPhoneiPod}
                  isIE11={isIE11}
                  toggleControlsVisibility={toggleControlsVisibility}
                  showControls={showControls}
                />
              </ControlsWrapper>
            </Suspense>
          ) : (
            ""
          )}
          {utils.whiteLabelChecker(whiteLabel) && !animationPlaying && <WhiteLabel className="whitelabel" />}
          {(isIE && !noMessage) && <IeMessageBanner whitelabel={whiteLabel} play={this.play} />}
        </ScreenWrapper>
      </Wrapper>
    ) : (
      ""
    );
  };
}


export default LoadOptions;
